import React, { useState, useRef, useEffect } from "react";
import { useParams } from "react-router-dom";
import client from "../../../API";
import { useQuery } from "react-query";
import { 
  Typography, 
  Box, 
  Container, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  CircularProgress, 
  Backdrop, 
  Button, 
  useMediaQuery, 
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  Paper,
  Stack,
  Slide,
  Fade,
  Zoom,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { alpha, useTheme } from "@mui/material/styles";

// Icons
import { 
  GridView, 
  ViewList, 
  Rocket, 
  Star, 
  TrendingUp, 
  Verified, 
  Public,
  QrCode,
  Animation,
  Share,
  Launch,
  Search,
  FilterList,
  Sort,
  LocationOn,
  Email,
  Phone,
  Language,
  Business,
  AutoGraph,
  Groups,
  Visibility,
  Close,
  CloudUpload
} from "@mui/icons-material";

// Social Media Icons
import { FaYoutube, FaInstagram, FaTiktok } from "react-icons/fa";

// Components
import useIsDesktop from "../../../Hooks/useIsDesktop.js";
import CRMDialog from "../../Misc/crmComponents/crmPopup.js";
import HomeHeader from "../../../Components/HomeHeader.js";
import BookingDialog from "../../Misc/bookingdialog";
import { platformRatesListFromCreator } from "../../../Utils/constants.js";
import { handleAvatarError } from "../../../Utils";
import profilePhoto from "../../../Components/globalAssets/ppfLogo.png";
import { toast } from 'sonner';

/*********************************************************************
 * Enhanced Animations with Performance Optimization
 *********************************************************************/
const fadeInAnimation = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideUpAnimation = keyframes`
  from { opacity: 0; transform: translateY(40px) scale(0.95); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const bounceInAnimation = keyframes`
  0% { opacity: 0; transform: scale(0.3) translateY(50px); }
  50% { opacity: 1; transform: scale(1.05) translateY(-10px); }
  70% { transform: scale(0.95) translateY(5px); }
  100% { opacity: 1; transform: scale(1) translateY(0); }
`;

const zoomInAnimation = keyframes`
  from { opacity: 0; transform: scale(0.5); }
  to { opacity: 1; transform: scale(1); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

/*********************************************************************
 * Enhanced Styled Components
 *********************************************************************/
const AnimatedWrapper = styled('div')(({ theme, animationType, animationsEnabled, delay = 0 }) => {
  if (!animationsEnabled) return {};
  
  const animationMap = {
    'FADE_IN': `${fadeInAnimation} 0.8s ease-out ${delay}ms both`,
    'SLIDE_UP': `${slideUpAnimation} 0.6s ease-out ${delay}ms both`,
    'BOUNCE_IN': `${bounceInAnimation} 0.8s ease-out ${delay}ms both`,
    'ZOOM_IN': `${zoomInAnimation} 0.6s ease-out ${delay}ms both`,
  };

  return {
    animation: animationMap[animationType] || animationMap['FADE_IN'],
  };
});

const EnhancedHeroSection = styled(Box)(({ theme, mainColor, accentColor, headerMedia }) => ({
  position: "relative",
  minHeight: "75vh",
  display: "flex",
  alignItems: "center",
  overflow: "hidden",
  background: headerMedia ? 'transparent' : `linear-gradient(135deg, ${mainColor || '#6F72FF'} 0%, ${accentColor || '#BA88FF'} 100%)`,
  
  '&::before': headerMedia ? {} : {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `radial-gradient(circle at 30% 20%, ${alpha(theme.palette.common.white, 0.1)} 0%, transparent 50%),
                 radial-gradient(circle at 70% 80%, ${alpha(theme.palette.common.white, 0.08)} 0%, transparent 50%)`,
  },
  
  '&::after': {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: "100px",
    background: `linear-gradient(to top, ${theme.palette.background.default}, transparent)`,
    zIndex: 2,
  },
}));

const HeroMediaBackground = styled('div')(({ mediaUrl, isVideo }) => ({
  position: 'absolute',
  inset: 0,
  zIndex: 0,
  
  ...(isVideo ? {
    '& video': {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    }
  } : {
    backgroundImage: `url(${mediaUrl})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundAttachment: 'fixed',
  }),
  
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(135deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.6) 100%)',
    backdropFilter: 'blur(1px)',
  },
}));

const FloatingStatsContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'overlay',
})(({ theme, overlay }) => ({
  // Default: inline block (no overlap)
  position: 'relative',
  zIndex: 1,
  display: 'flex',
  flexDirection: 'row',
  gap: theme.spacing(2),
  flexWrap: 'wrap',
  justifyContent: 'center',
  marginTop: theme.spacing(4),

  // If you *want* overlay (e.g., video/image header), enable with prop
  ...(overlay && {
    position: 'absolute',
    bottom: '8%',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 3,
    marginTop: 0,
  }),

  // On smaller screens, always fall back to inline to avoid collisions
  [theme.breakpoints.down('lg')]: {
    position: 'relative',
    bottom: 'auto',
    left: 'auto',
    transform: 'none',
    zIndex: 1,
    marginTop: theme.spacing(4),
  },
}));


const GlassStatsCard = styled(Paper)(({ theme, animationsEnabled }) => ({
  background: 'rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  borderRadius: '20px',
  padding: theme.spacing(3),
  textAlign: 'center',
  color: 'white',
  minWidth: 150,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  animation: animationsEnabled ? `${float} 6s ease-in-out infinite` : 'none',
  
  '&:hover': {
    transform: animationsEnabled ? 'translateY(-5px) scale(1.05)' : 'translateY(-2px)',
    background: 'rgba(255, 255, 255, 0.15)',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
  },
}));

const ModernCompanyLogo = styled('img')(({ theme, animationsEnabled }) => ({
  height: 80,
  maxWidth: 200,
  objectFit: 'contain',
  borderRadius: '16px',
  boxShadow: `0 10px 30px ${alpha(theme.palette.common.black, 0.3)}`,
  border: `3px solid ${alpha(theme.palette.common.white, 0.2)}`,
  backdropFilter: 'blur(10px)',
  marginBottom: theme.spacing(3),
  animation: animationsEnabled ? `${float} 8s ease-in-out infinite` : 'none',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  
  '&:hover': {
    transform: animationsEnabled ? 'rotate(-2deg) scale(1.05)' : 'scale(1.02)',
    boxShadow: `0 15px 40px ${alpha(theme.palette.common.black, 0.4)}`,
  },
}));

const EnhancedSearchFilterBar = styled(Paper)(({ theme, mainColor }) => ({
  background: `linear-gradient(145deg, 
    ${alpha(theme.palette.background.paper, 0.95)}, 
    ${alpha(mainColor || theme.palette.primary.main, 0.02)}
  )`,
  backdropFilter: "blur(20px)",
  borderRadius: "24px",
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  padding: theme.spacing(3),
  marginBottom: theme.spacing(4),
  display: "flex",
  gap: theme.spacing(2),
  flexWrap: "wrap",
  alignItems: "center",
  position: "relative",
  overflow: "hidden",
  
  '&::before': {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "2px",
    background: `linear-gradient(90deg, transparent, ${mainColor || theme.palette.primary.main}, transparent)`,
    animation: `${shimmer} 3s ease-in-out infinite`,
  },
}));

const ModernCreatorCard = styled(Card)(({ theme, mainColor, accentColor, animationsEnabled, viewType }) => ({
  borderRadius: viewType === 'COMPLEX' ? '24px' : '16px',
  background: `linear-gradient(145deg, 
    ${alpha(theme.palette.background.paper, 0.95)}, 
    ${alpha(theme.palette.background.paper, 0.9)}
  )`,
  backdropFilter: 'blur(20px)',
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  overflow: 'hidden',
  position: 'relative',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  cursor: 'pointer',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: `linear-gradient(90deg, ${mainColor || theme.palette.primary.main}, ${accentColor || theme.palette.secondary.main})`,
    transform: 'scaleX(0)',
    transformOrigin: 'left',
    transition: 'transform 0.4s ease',
  },
  
  '&:hover': {
    transform: animationsEnabled ? 'translateY(-8px) scale(1.02)' : 'translateY(-4px)',
    boxShadow: `0 20px 40px ${alpha(mainColor || theme.palette.primary.main, 0.15)}`,
    
    '&::before': {
      transform: 'scaleX(1)',
    },
    
    '& .creator-avatar': {
      transform: 'scale(1.1)',
    },
    
    '& .platform-chip': {
      transform: 'translateY(-2px)',
    },
  },
}));

const SocialPlatformChip = styled(Chip)(({ theme, platform, platformColor }) => {
  const platformColors = {
    youtube: '#FF0000',
    instagram: '#E4405F', 
    tiktok: '#000000',
  };

  const color = platformColor || platformColors[platform] || theme.palette.primary.main;

  return {
    borderRadius: '12px',
    fontWeight: 600,
    fontSize: '0.75rem',
    transition: 'all 0.3s ease',
    border: `2px solid ${alpha(color, 0.2)}`,
    backgroundColor: alpha(color, 0.1),
    color: color,
    
    '&:hover': {
      backgroundColor: alpha(color, 0.2),
      transform: 'translateY(-2px)',
      boxShadow: `0 8px 16px ${alpha(color, 0.3)}`,
    },
    
    '& .MuiChip-avatar': {
      color: color,
      backgroundColor: 'transparent',
    },
  };
});

const ModernToggleGroup = styled(ToggleButtonGroup)(({ theme, mainColor }) => ({
  background: alpha(mainColor || theme.palette.primary.main, 0.1),
  borderRadius: '16px',
  padding: '6px',
  backdropFilter: 'blur(10px)',
  border: `1px solid ${alpha(mainColor || theme.palette.primary.main, 0.2)}`,
  
  '& .MuiToggleButton-root': {
    border: 'none',
    borderRadius: '12px !important',
    color: alpha(theme.palette.text.primary, 0.8),
    fontWeight: 600,
    textTransform: 'none',
    padding: theme.spacing(1.5, 3),
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    
    '&.Mui-selected': {
      backgroundColor: mainColor || theme.palette.primary.main,
      color: theme.palette.common.white,
      boxShadow: `0 8px 16px ${alpha(mainColor || theme.palette.primary.main, 0.3)}`,
      
      '&:hover': {
        backgroundColor: alpha(mainColor || theme.palette.primary.main, 0.8),
      },
    },
    
    '&:hover': {
      backgroundColor: alpha(mainColor || theme.palette.primary.main, 0.1),
    },
  },
}));

const ActionButton = styled(Button)(({ theme, variant, bgcolor }) => ({
  borderRadius: '12px',
  fontWeight: 600,
  textTransform: 'none',
  padding: theme.spacing(1.5, 3),
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: `linear-gradient(90deg, transparent, ${alpha(theme.palette.common.white, 0.2)}, transparent)`,
    transition: 'left 0.5s ease',
  },
  
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: variant === 'contained' 
      ? `0 8px 25px ${alpha(bgcolor || theme.palette.primary.main, 0.4)}`
      : `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
    
    '&::before': {
      left: '100%',
    },
  },
  
  ...(variant === 'contained' && bgcolor && {
    background: `linear-gradient(135deg, ${bgcolor}, ${alpha(bgcolor, 0.8)})`,
    
    '&:hover': {
      background: `linear-gradient(135deg, ${alpha(bgcolor, 0.9)}, ${alpha(bgcolor, 0.7)})`,
    },
  }),
}));

const GlassContentPanel = styled(Paper)(({ theme }) => ({
  background: `linear-gradient(145deg, 
    ${alpha(theme.palette.background.paper, 0.95)}, 
    ${alpha(theme.palette.background.paper, 0.8)}
  )`,
  backdropFilter: 'blur(20px)',
  borderRadius: '24px',
  border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
  overflow: 'hidden',
  position: 'relative',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '1px',
    background: `linear-gradient(90deg, transparent, ${alpha(theme.palette.common.white, 0.5)}, transparent)`,
  },
}));

const LoadingScreen = styled(Box)(({ theme, mainColor, accentColor }) => ({
  width: "100vw",
  height: "100vh",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  background: `linear-gradient(135deg, ${mainColor || '#6F72FF'}, ${accentColor || '#BA88FF'})`,
  color: theme.palette.common.white,
}));

/*********************************************************************
 * Constants
 *********************************************************************/
const PLATFORM_ICONS = {
  youtube: FaYoutube,
  instagram: FaInstagram,
  tiktok: FaTiktok,
};

const PLATFORM_COLORS = {
  youtube: "#FF0000",
  instagram: "#E4405F", 
  tiktok: "#000000",
};

/*********************************************************************
 * Main Component
 *********************************************************************/
const EnhancedAgencyPageDesktop = () => {
  const theme = useTheme();
  const { manager } = useParams();
  const [agencyDetails, setAgencyDetails] = useState(null);
  const [rosterSettings, setRosterSettings] = useState(null);
  const [viewMode, setViewMode] = useState("list");
  const [showCRMDialog, setShowCRMDialog] = useState(true);
  const [showInstructions, setShowInstructions] = useState(false);
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [selectedCreator, setSelectedCreator] = useState(null);
  const [promotionData, setPromotionData] = useState([]);
  const [isLoadingCreator, setIsLoadingCreator] = useState(false);
  
  // Enhanced filtering state
  const [searchQuery, setSearchQuery] = useState("");
  const [platformFilter, setPlatformFilter] = useState("all");
  const [sortOption, setSortOption] = useState("reachDesc");
  
  const isDesktop = useIsDesktop();
  const hasShownMessage = useRef(false);

  useEffect(() => {
    setViewMode(isDesktop ? "list" : "grid");
  }, [isDesktop]);

  // Fetch roster settings with enhanced error handling
  const { isLoading: isLoadingSettings } = useQuery(
    ["publicRosterSettings", manager],
    () => client.agency.getPublicRosterSettings(manager),
    {
      onSuccess: (res) => {
        setRosterSettings(res);
        console.log('Loaded roster settings:', res);
      },
      onError: (err) => {
        if (err?.response?.status !== 404) {
          console.error("Failed to fetch public roster settings:", err);
        }
        // Set default settings if none found
        setRosterSettings({
          name: `Creator Roster by: ${manager}`,
          description: "A curated list of creators and all their social stats.",
          color_scheme: {
            main: '#6F72FF',
            accent: '#BA88FF',
            backgroundColor: '#F8FAFF'
          },
          animations_enabled: true,
          animation_type: 'FADE_IN',
          view_type: 'SIMPLE',
          show_stats: true,
          show_contact_info: false,
        });
      },
      refetchOnWindowFocus: false,
    }
  );

  const companyName = rosterSettings?.company?.name || manager;
  
  const { data, isLoading: isLoadingCreators } = useQuery(
    ["agencyDetails", companyName],
    () => client.creators.listManager(companyName),
    {
      enabled: !!companyName && !!rosterSettings, // Only run after settings are loaded
      onSuccess: (data) => setAgencyDetails({ ...data }),
      onError: (error) => console.error("Failed to fetch agency details:", error),
      refetchOnWindowFocus: false,
    }
  );

  // Enhanced creator interaction handlers
  const handleInviteClick = async (creatorName) => {
    setIsLoadingCreator(true);
    try {
      const creatorData = await client.creators.fetchDetails(creatorName);
      setSelectedCreator(creatorData.creator);
      setPromotionData(platformRatesListFromCreator(creatorData.creator));
      setBookingDialogOpen(true);
    } catch (error) {
      console.error("Failed to fetch creator details:", error);
      toast.error("Failed to fetch creator details. Please try again.");
    } finally {
      setIsLoadingCreator(false);
    }
  };

  const handleBookingSubmission = async (bookingDetails) => {
    try {
      const result = await client.partnerships.book({
        name: `${bookingDetails.partnershipName} - ${bookingDetails.selectedPromotion}`,
        creator: bookingDetails.creatorId,
        description: bookingDetails.details,
        email: bookingDetails.email,
        proposal_date: bookingDetails.selectedDate,
        total_sum: bookingDetails.rate,
        unique_code: `promo_${bookingDetails.creatorId}_${new Date().getTime()}`,
        notes: bookingDetails.details,
        finish_date: bookingDetails.selectedDate,
      });
      toast.success('Booking successful', {
        description: `Agency: ${result?.agency_name || 'Unknown'}`
      });
      setBookingDialogOpen(false);
      return true;
    } catch (error) {
      console.error("Error submitting booking:", error);
      toast.error(`Error submitting booking: ${error?.response?.data?.error || error?.message}`);
      return false;
    }
  };

  const handleChangeView = (event, newValue) => {
    if (newValue !== null) {
      setViewMode(newValue);
    }
  };

  const handleCloseCRMDialog = () => setShowCRMDialog(false);

  // Enhanced creator filtering and sorting
  const calculateTotalReach = (creator) => {
    return [
      parseInt(creator.youtube) || 0,
      parseInt(creator.instagram) || 0,
      parseInt(creator.tiktok) || 0,
    ].reduce((acc, val) => acc + val, 0);
  };

  const getFilteredSortedCreators = () => {
    if (!agencyDetails?.creators) return [];
    
    let filtered = [...agencyDetails.creators];

    if (searchQuery.trim() !== "") {
      filtered = filtered.filter((c) =>
        c.creator.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.display_name?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (platformFilter !== "all") {
      filtered = filtered.filter((c) => {
        switch (platformFilter) {
          case "youtube":
            return parseInt(c.youtube) > 0;
          case "instagram":
            return parseInt(c.instagram) > 0;
          case "tiktok":
            return parseInt(c.tiktok) > 0;
          default:
            return true;
        }
      });
    }

    return filtered.sort((a, b) => {
      const totalA = calculateTotalReach(a);
      const totalB = calculateTotalReach(b);
      switch (sortOption) {
        case "nameAsc":
          return a.creator.localeCompare(b.creator);
        case "nameDesc":
          return b.creator.localeCompare(a.creator);
        case "reachAsc":
          return totalA - totalB;
        case "reachDesc":
          return totalB - totalA;
        default:
          return 0;
      }
    });
  };

  const filteredCreators = getFilteredSortedCreators();

  // Animation helper
  const getAnimationDelay = (index) => rosterSettings?.animations_enabled ? index * 150 : 0;

  // Enhanced creator card rendering
  const renderCreatorCard = (creator, index) => {
    const totalReach = calculateTotalReach(creator);
    const delay = getAnimationDelay(index);

    return (
      <Grid 
        item 
        xs={12} 
        sm={viewMode === 'grid' ? 6 : 12} 
        md={viewMode === 'grid' ? 4 : 12} 
        lg={viewMode === 'grid' && rosterSettings?.view_type === 'COMPLEX' ? 3 : 12} 
        key={creator.creator || index}
      >
        <AnimatedWrapper
          animationType={rosterSettings?.animation_type}
          animationsEnabled={rosterSettings?.animations_enabled}
          delay={delay}
        >
          <ModernCreatorCard
            mainColor={rosterSettings?.color_scheme?.main}
            accentColor={rosterSettings?.color_scheme?.accent}
            animationsEnabled={rosterSettings?.animations_enabled}
            viewType={rosterSettings?.view_type}
          >
            <CardContent sx={{ p: 3 }}>
              {viewMode === 'grid' ? (
                // Grid View Layout
                <Box sx={{ textAlign: 'center' }}>
                  <Avatar
                    src={creator.pfphref || profilePhoto}
                    alt={creator.creator}
                    className="creator-avatar"
                    onError={handleAvatarError(profilePhoto)}
                    sx={{
                      width: 100,
                      height: 100,
                      margin: '0 auto',
                      mb: 2,
                      boxShadow: `0 12px 24px ${alpha(rosterSettings?.color_scheme?.main || theme.palette.primary.main, 0.3)}`,
                      border: `4px solid ${alpha(rosterSettings?.color_scheme?.main || theme.palette.primary.main, 0.2)}`,
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  />
                  
                  <Typography variant="h6" fontWeight={700} gutterBottom sx={{
                    background: `linear-gradient(45deg, ${theme.palette.text.primary}, ${rosterSettings?.color_scheme?.main || theme.palette.primary.main})`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}>
                    @{creator.display_name || creator.creator}
                  </Typography>

                  {rosterSettings?.show_stats && (
                    <Chip
                      icon={<TrendingUp />}
                      label={`${totalReach.toLocaleString()} Total Reach`}
                      sx={{
                        mb: 2,
                        bgcolor: alpha(rosterSettings?.color_scheme?.main || theme.palette.primary.main, 0.1),
                        color: rosterSettings?.color_scheme?.main || theme.palette.primary.main,
                        fontWeight: 600,
                        borderRadius: '12px',
                      }}
                    />
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                    {creator.youtube && (
                      <SocialPlatformChip
                        component={creator.youtube_link ? "a" : "div"}
                        href={creator.youtube_link}
                        target={creator.youtube_link ? "_blank" : undefined}
                        clickable={!!creator.youtube_link}
                        className="platform-chip"
                        platform="youtube"
                        avatar={<PLATFORM_ICONS.youtube />}
                        label={parseInt(creator.youtube).toLocaleString()}
                        size="small"
                      />
                    )}
                    {creator.instagram && (
                      <SocialPlatformChip
                        component={creator.instagram_link ? "a" : "div"}
                        href={creator.instagram_link}
                        target={creator.instagram_link ? "_blank" : undefined}
                        clickable={!!creator.instagram_link}
                        className="platform-chip"
                        platform="instagram"
                        avatar={<PLATFORM_ICONS.instagram />}
                        label={parseInt(creator.instagram).toLocaleString()}
                        size="small"
                      />
                    )}
                    {creator.tiktok && (
                      <SocialPlatformChip
                        component={creator.tiktok_link ? "a" : "div"}
                        href={creator.tiktok_link}
                        target={creator.tiktok_link ? "_blank" : undefined}
                        clickable={!!creator.tiktok_link}
                        className="platform-chip"
                        platform="tiktok"
                        avatar={<PLATFORM_ICONS.tiktok />}
                        label={parseInt(creator.tiktok).toLocaleString()}
                        size="small"
                      />
                    )}
                  </Box>

                  {rosterSettings?.show_contact_info && (
                    <Box sx={{ mb: 2, opacity: 0.8 }}>
                      {creator.email && (
                        <Typography variant="caption" display="block">
                          <Email sx={{ fontSize: 12, mr: 0.5 }} />
                          {creator.email}
                        </Typography>
                      )}
                      {creator.phone_number && (
                        <Typography variant="caption" display="block">
                          <Phone sx={{ fontSize: 12, mr: 0.5 }} />
                          {creator.phone_number}
                        </Typography>
                      )}
                    </Box>
                  )}

                  <Stack spacing={1}>
                    <ActionButton
                      variant="contained"
                      bgcolor={rosterSettings?.color_scheme?.main}
                      onClick={() => handleInviteClick(creator.creator)}
                      fullWidth
                    >
                      Collaborate
                    </ActionButton>
                    <ActionButton
                      variant="outlined"
                      href={`/creators/${creator.creator}`}
                      target="_blank"
                      startIcon={<Launch />}
                      sx={{ 
                        borderColor: rosterSettings?.color_scheme?.accent, 
                        color: rosterSettings?.color_scheme?.accent 
                      }}
                      fullWidth
                    >
                      Media Kit
                    </ActionButton>
                  </Stack>
                </Box>
              ) : (
                // List View Layout
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <Avatar
                    src={creator.pfphref || profilePhoto}
                    className="creator-avatar"
                    onError={handleAvatarError(profilePhoto)}
                    sx={{
                      width: 80,
                      height: 80,
                      boxShadow: `0 8px 24px ${alpha(rosterSettings?.color_scheme?.main || theme.palette.primary.main, 0.3)}`,
                      border: `3px solid ${alpha(rosterSettings?.color_scheme?.main || theme.palette.primary.main, 0.2)}`,
                      transition: 'all 0.3s ease',
                    }}
                  />

                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" fontWeight={700} sx={{ 
                      background: `linear-gradient(45deg, ${theme.palette.text.primary}, ${rosterSettings?.color_scheme?.main || theme.palette.primary.main})`,
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}>
                      @{creator.display_name || creator.creator}
                    </Typography>

                    {rosterSettings?.show_stats && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                        <TrendingUp fontSize="small" sx={{ color: rosterSettings?.color_scheme?.accent }} />
                        <Typography variant="body2" color="text.secondary" fontWeight={500}>
                          {totalReach.toLocaleString()} total reach
                        </Typography>
                      </Box>
                    )}

                    {rosterSettings?.show_contact_info && (
                      <Box sx={{ mt: 1, opacity: 0.8 }}>
                        {creator.email && (
                          <Typography variant="body2" color="text.secondary">
                            <Email sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                            {creator.email}
                          </Typography>
                        )}
                      </Box>
                    )}

                    {creator.youtube_link && (
                      <Typography
                        component="a"
                        href={creator.youtube_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="body2"
                        sx={{ 
                          mt: 1,
                          color: rosterSettings?.color_scheme?.accent,
                          textDecoration: 'none',
                          fontWeight: 500,
                          display: 'block',
                          '&:hover': { textDecoration: 'underline' }
                        }}
                      >
                        <Language sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                        YouTube Channel
                      </Typography>
                    )}
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {creator.youtube && (
                        <SocialPlatformChip
                          component={creator.youtube_link ? "a" : "div"}
                          href={creator.youtube_link}
                          target={creator.youtube_link ? "_blank" : undefined}
                          clickable={!!creator.youtube_link}
                          className="platform-chip"
                          platform="youtube"
                          avatar={<PLATFORM_ICONS.youtube />}
                          label={parseInt(creator.youtube).toLocaleString()}
                          size="small"
                        />
                      )}
                      {creator.instagram && (
                        <SocialPlatformChip
                          component={creator.instagram_link ? "a" : "div"}
                          href={creator.instagram_link}
                          target={creator.instagram_link ? "_blank" : undefined}
                          clickable={!!creator.instagram_link}
                          className="platform-chip"
                          platform="instagram"
                          avatar={<PLATFORM_ICONS.instagram />}
                          label={parseInt(creator.instagram).toLocaleString()}
                          size="small"
                        />
                      )}
                      {creator.tiktok && (
                        <SocialPlatformChip
                          component={creator.tiktok_link ? "a" : "div"}
                          href={creator.tiktok_link}
                          target={creator.tiktok_link ? "_blank" : undefined}
                          clickable={!!creator.tiktok_link}
                          className="platform-chip"
                          platform="tiktok"
                          avatar={<PLATFORM_ICONS.tiktok />}
                          label={parseInt(creator.tiktok).toLocaleString()}
                          size="small"
                        />
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <ActionButton
                        variant="outlined"
                        href={`/creators/${creator.creator}`}
                        target="_blank"
                        startIcon={<Launch />}
                        sx={{ 
                          borderColor: rosterSettings?.color_scheme?.accent, 
                          color: rosterSettings?.color_scheme?.accent 
                        }}
                      >
                        Media Kit
                      </ActionButton>
                      <ActionButton
                        variant="contained"
                        bgcolor={rosterSettings?.color_scheme?.main}
                        onClick={() => handleInviteClick(creator.creator)}
                      >
                        Collaborate
                      </ActionButton>
                    </Box>
                  </Box>
                </Box>
              )}
            </CardContent>
          </ModernCreatorCard>
        </AnimatedWrapper>
      </Grid>
    );
  };

  if (isLoadingCreators || !agencyDetails || isLoadingSettings) {
    return (
      <LoadingScreen
        mainColor={rosterSettings?.color_scheme?.main}
        accentColor={rosterSettings?.color_scheme?.accent}
      >
        <AnimatedWrapper
          animationType="FADE_IN"
          animationsEnabled={true}
          delay={0}
        >
          <CircularProgress 
            size={80} 
            thickness={4} 
            sx={{ mb: 3, color: 'common.white' }} 
          />
        </AnimatedWrapper>
        <AnimatedWrapper
          animationType="SLIDE_UP"
          animationsEnabled={true}
          delay={200}
        >
          <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
            Loading Creator Roster
          </Typography>
        </AnimatedWrapper>
        <AnimatedWrapper
          animationType="SLIDE_UP"
          animationsEnabled={true}
          delay={400}
        >
          <Typography variant="h6" sx={{ opacity: 0.9 }}>
            Preparing the ultimate creator experience...
          </Typography>
        </AnimatedWrapper>
      </LoadingScreen>
    );
  }

  const mainColor = rosterSettings?.color_scheme?.main || "#6F72FF";
  const accentColor = rosterSettings?.color_scheme?.accent || "#BA88FF";
  const backgroundColor = rosterSettings?.color_scheme?.backgroundColor || "#F8FAFF";
  const rosterName = rosterSettings?.name || `Creator Roster by: ${manager}`;
  const rosterDescription = rosterSettings?.description || "A curated list of creators and all their social stats.";
  const rosterLogo = rosterSettings?.logo;
  const headerMedia = rosterSettings?.header_media_url;
  const isHeaderVideo = headerMedia && /\.(mp4|webm|ogg)$/i.test(headerMedia);
  
  const totalCreators = agencyDetails?.creators?.length || 0;
  const totalReach = agencyDetails?.creators?.reduce((sum, creator) => {
    return sum + calculateTotalReach(creator);
  }, 0) || 0;

  const avgEngagement = totalCreators > 0 ? Math.round(totalReach / totalCreators) : 0;
  const platformCoverage = new Set(
    agencyDetails?.creators?.flatMap(c => [
      c.youtube && 'YouTube',
      c.instagram && 'Instagram', 
      c.tiktok && 'TikTok'
    ].filter(Boolean))
  ).size;

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: backgroundColor }}>
      <HomeHeader variant="minimal" />

      {/* Enhanced Hero Section */}
      <EnhancedHeroSection 
        mainColor={mainColor} 
        accentColor={accentColor}
        headerMedia={!!headerMedia}
      >
        {headerMedia && (
          <HeroMediaBackground mediaUrl={headerMedia} isVideo={isHeaderVideo}>
            {isHeaderVideo && (
              <video autoPlay muted loop>
                <source src={headerMedia} type="video/mp4" />
              </video>
            )}
          </HeroMediaBackground>
        )}

        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 3 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} lg={8}>
              <AnimatedWrapper
                animationType={rosterSettings?.animation_type}
                animationsEnabled={rosterSettings?.animations_enabled}
                delay={200}
              >
                <Box>
                  {rosterLogo && (
                    <ModernCompanyLogo
                      src={rosterLogo}
                      alt="Agency Logo"
                      animationsEnabled={rosterSettings?.animations_enabled}
                    />
                  )}
                  
                  <Typography 
                    variant="h1" 
                    sx={{ 
                      fontWeight: 900, 
                      fontSize: { xs: '2.5rem', md: '4rem', lg: '5rem' },
                      letterSpacing: '-0.02em', 
                      color: 'common.white', 
                      mb: 2,
                      lineHeight: 1.1,
                      textShadow: `0 4px 8px ${alpha(theme.palette.common.black, 0.3)}`,
                    }}
                  >
                    {rosterName}
                  </Typography>
                  
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      color: alpha(theme.palette.common.white, 0.95), 
                      lineHeight: 1.6, 
                      mb: 4,
                      maxWidth: 700,
                      fontSize: { xs: '1.1rem', md: '1.5rem' },
                      fontWeight: 400,
                    }}
                  >
                    {rosterDescription}
                  </Typography>

                  <Box sx={{ 
                    display: 'flex', 
                    gap: 2, 
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'stretch', sm: 'center' }
                  }}>
                    <ModernToggleGroup
                      value={viewMode}
                      exclusive
                      onChange={handleChangeView}
                      mainColor={mainColor}
                    >
                      <ToggleButton value="list">
                        <ViewList sx={{ mr: 1 }} /> 
                        List View
                      </ToggleButton>
                      <ToggleButton value="grid">
                        <GridView sx={{ mr: 1 }} /> 
                        Grid View
                      </ToggleButton>
                    </ModernToggleGroup>

                    <ActionButton
                      variant="outlined"
                      onClick={() => setShowInstructions(true)}
                      sx={{
                        color: 'common.white',
                        borderColor: alpha(theme.palette.common.white, 0.3),
                        '&:hover': {
                          borderColor: 'common.white',
                          backgroundColor: alpha(theme.palette.common.white, 0.1),
                        },
                      }}
                      startIcon={<Rocket />}
                    >
                      Quick Tour
                    </ActionButton>
                  </Box>
                </Box>
              </AnimatedWrapper>
            </Grid>

            <Grid item xs={12} lg={4}>
              <FloatingStatsContainer>
                {[
                  { 
                    label: 'Creators', 
                    value: totalCreators, 
                    icon: <Groups />, 
                    delay: 400 
                  },
                  { 
                    label: 'Total Reach', 
                    value: `${(totalReach / 1000000).toFixed(1)}M`, 
                    icon: <TrendingUp />, 
                    delay: 500 
                  },
                  { 
                    label: 'Avg. Reach', 
                    value: `${(avgEngagement / 1000).toFixed(0)}K`, 
                    icon: <AutoGraph />, 
                    delay: 600 
                  },
                  { 
                    label: 'Platforms', 
                    value: platformCoverage, 
                    icon: <Public />, 
                    delay: 700 
                  },
                ].map((stat, index) => (
                  <AnimatedWrapper
                    key={stat.label}
                    animationType={rosterSettings?.animation_type}
                    animationsEnabled={rosterSettings?.animations_enabled}
                    delay={stat.delay}
                  >
                    <GlassStatsCard animationsEnabled={rosterSettings?.animations_enabled}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                        {stat.icon}
                      </Box>
                      <Typography variant="h3" fontWeight={800} sx={{ color: 'common.white', mb: 0.5 }}>
                        {stat.value}
                      </Typography>
                      <Typography variant="body2" sx={{ color: alpha(theme.palette.common.white, 0.8) }}>
                        {stat.label}
                      </Typography>
                    </GlassStatsCard>
                  </AnimatedWrapper>
                ))}
              </FloatingStatsContainer>
            </Grid>
          </Grid>
        </Container>
      </EnhancedHeroSection>

      {/* Enhanced Main Content */}
      <Container maxWidth="xl" sx={{ mt: -4, pb: 6, position: 'relative', zIndex: 3 }}>
        <AnimatedWrapper
          animationType={rosterSettings?.animation_type}
          animationsEnabled={rosterSettings?.animations_enabled}
          delay={800}
        >
          <GlassContentPanel elevation={0}>
            <Box sx={{ p: { xs: 2, md: 4 } }}>
              {/* Enhanced Search and Filter Bar */}
              <EnhancedSearchFilterBar mainColor={mainColor}>
                <TextField
                  placeholder="Search creators..."
                  variant="outlined"
                  size="small"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ 
                    width: { xs: '100%', sm: 300 },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      backgroundColor: alpha(theme.palette.background.paper, 0.8),
                    }
                  }}
                />

                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <FilterList fontSize="small" />
                      Platform
                    </Box>
                  </InputLabel>
                  <Select
                    value={platformFilter}
                    onChange={(e) => setPlatformFilter(e.target.value)}
                    sx={{ 
                      borderRadius: '12px',
                      backgroundColor: alpha(theme.palette.background.paper, 0.8),
                    }}
                  >
                    <MenuItem value="all">All Platforms</MenuItem>
                    <MenuItem value="youtube">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FaYoutube color="#FF0000" />
                        YouTube
                      </Box>
                    </MenuItem>
                    <MenuItem value="instagram">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FaInstagram color="#E4405F" />
                        Instagram
                      </Box>
                    </MenuItem>
                    <MenuItem value="tiktok">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FaTiktok color="#000000" />
                        TikTok
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Sort fontSize="small" />
                      Sort By
                    </Box>
                  </InputLabel>
                  <Select
                    value={sortOption}
                    onChange={(e) => setSortOption(e.target.value)}
                    sx={{ 
                      borderRadius: '12px',
                      backgroundColor: alpha(theme.palette.background.paper, 0.8),
                    }}
                  >
                    <MenuItem value="nameAsc">Name (A-Z)</MenuItem>
                    <MenuItem value="nameDesc">Name (Z-A)</MenuItem>
                    <MenuItem value="reachAsc">Reach ↑</MenuItem>
                    <MenuItem value="reachDesc">Reach ↓</MenuItem>
                  </Select>
                </FormControl>

                <Chip 
                  label={`${filteredCreators.length} creators`}
                  sx={{
                    backgroundColor: alpha(mainColor, 0.1),
                    color: mainColor,
                    fontWeight: 600,
                    borderRadius: '12px',
                    border: `2px solid ${alpha(mainColor, 0.2)}`,
                  }}
                />
              </EnhancedSearchFilterBar>

              {/* Creators Grid */}
              {filteredCreators.length === 0 ? (
                <AnimatedWrapper
                  animationType={rosterSettings?.animation_type}
                  animationsEnabled={rosterSettings?.animations_enabled}
                  delay={1000}
                >
                  <Box sx={{ textAlign: 'center', py: 10 }}>
                    <Typography variant="h4" color="text.secondary" fontWeight={600} gutterBottom>
                      No creators match your filters
                    </Typography>
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
                      Try adjusting your search criteria or browse all creators
                    </Typography>
                    <ActionButton
                      variant="contained"
                      bgcolor={mainColor}
                      onClick={() => {
                        setSearchQuery('');
                        setPlatformFilter('all');
                      }}
                    >
                      Clear Filters
                    </ActionButton>
                  </Box>
                </AnimatedWrapper>
              ) : (
                <Grid container spacing={3}>
                  {filteredCreators.map((creator, index) => renderCreatorCard(creator, index))}
                </Grid>
              )}

              {/* Enhanced Watermark */}
              <AnimatedWrapper
                animationType={rosterSettings?.animation_type}
                animationsEnabled={rosterSettings?.animations_enabled}
                delay={1200}
              >
                <Box sx={{ textAlign: 'center', mt: 8, py: 4 }}>
                  <Divider sx={{ 
                    mb: 3, 
                    background: `linear-gradient(90deg, transparent, ${alpha(mainColor, 0.3)}, transparent)`, 
                    height: 2, 
                    border: 'none' 
                  }} />
                  <Typography variant="h6" sx={{ 
                    fontWeight: 600, 
                    background: `linear-gradient(45deg, ${mainColor}, ${accentColor})`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}>
                    Powered by Blitz ✨
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    The ultimate creator collaboration platform
                  </Typography>
                </Box>
              </AnimatedWrapper>
            </Box>
          </GlassContentPanel>
        </AnimatedWrapper>
      </Container>

      {/* Dialogs and Modals */}
      <BookingDialog
        open={bookingDialogOpen}
        onClose={() => setBookingDialogOpen(false)}
        submitBooking={handleBookingSubmission}
        creator={selectedCreator}
        promotionData={promotionData}
      />

      {showCRMDialog && (
        <CRMDialog
          isOpen={showCRMDialog}
          handleClose={handleCloseCRMDialog}
          origin={`${manager} - roster`}
        />
      )}

      <Dialog 
        open={showInstructions} 
        onClose={() => setShowInstructions(false)}
        PaperProps={{
          sx: {
            borderRadius: '20px',
            background: `linear-gradient(145deg, ${theme.palette.background.paper}, ${alpha(mainColor, 0.02)})`,
            maxWidth: 600,
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 700, 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          background: `linear-gradient(45deg, ${mainColor}, ${accentColor})`,
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}>
          <Star /> Welcome to Our Creator Roster
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
            Explore our curated collection of talented creators and discover your perfect collaboration partner.
          </Typography>
          <Grid container spacing={2}>
            {[
              {
                icon: <GridView />,
                title: 'Switch Views',
                desc: 'Toggle between List and Grid views for optimal browsing'
              },
              {
                icon: <Search />,
                title: 'Smart Search',
                desc: 'Filter by platform, reach, or creator name instantly'
              },
              {
                icon: <Launch />,
                title: 'Media Kits',
                desc: 'Access detailed creator profiles and performance metrics'
              },
              {
                icon: <Business />,
                title: 'Direct Collaboration',
                desc: 'Start partnerships with one-click booking system'
              }
            ].map((feature, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Box sx={{ display: 'flex', gap: 2, p: 2 }}>
                  <Box sx={{ color: mainColor, mt: 0.5 }}>
                    {feature.icon}
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {feature.desc}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <ActionButton
            onClick={() => setShowInstructions(false)}
            variant="contained"
            bgcolor={mainColor}
            sx={{ px: 4 }}
          >
            Let's Explore!
          </ActionButton>
        </DialogActions>
      </Dialog>

      <Backdrop
        sx={{ 
          color: '#fff', 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backdropFilter: 'blur(4px)'
        }}
        open={isLoadingCreator}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress color="inherit" size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading creator details...
          </Typography>
        </Box>
      </Backdrop>
    </Box>
  );
};

export default EnhancedAgencyPageDesktop;